import React, { createContext, useContext, useState } from 'react';
import styled from 'threads5/styles/styled';
import { Stack } from 'src/components-v2/Layout';
import type { ITheme } from 'threads5/themes/theme';
import SxProps from 'threads5/themes/sx-props';
import { useCriticalRenderContext } from 'src/hooks/useCriticalRenderContext';
import useMeasure from 'react-use-measure';
import useHasMouse from 'src/hooks/useHasMouse';
import useIsPotato from 'src/hooks/useIsPotato';
import BtnPrimary from 'src/components-v2/Inputs/Button/BtnPrimary';

const forbiddenProps = new Set(['variant', 'sx', 'fontSize']);
const shouldForwardProp = (prop: string) => {
  return !forbiddenProps.has(prop);
};

const Btn = styled('a', {
  shouldForwardProp,
})<IButton>(({ theme, variant = 'primary' }) => {
  const variantStyles = {
    primary: {
      background:
        'linear-gradient(108deg, #CDF5FD 0%, #D8FEF3 50%, #E8FED7 100%)',
      color: theme.palette['base/turquoise/800'],
      ':hover, :focus': {
        background: 'linear-gradient(266deg, #C0FFC0 -7.84%, #FFF 100%)',
      },
    },
    secondary: {
      background: '#02294B',
      color: theme.palette['base/black/100'],
      ':hover, :focus': {
        color: '#02294B',
        background:
          'linear-gradient(272deg, #B5EEDE 0%, #D3E4F8 33%, #E3DFFF 66%, #FAD7FF 100%)',
      },
    },
  };

  return {
    borderRadius: '46px',
    boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.15) inset',
    fontFamily: theme.typography.sans,
    fontSize: '1.8rem',
    fontStyle: 'normal',
    fontWeight: 600,
    letterSpacing: '-0.75px',
    height: '48px',
    padding: '12px 20px',
    textAlign: 'center',
    ...variantStyles[variant],
    [theme.breakpoints.up('md')]: {
      fontSize: '2.0rem',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '13px 32px',
      height: '56px',
    },
  };
});

interface IButton {
  variant?: 'primary' | 'secondary';
}

const ToggleContext = createContext<
  | {
      isWinking: boolean;
      setIsWinking: React.Dispatch<React.SetStateAction<boolean>>;
      isPsychedelic: boolean;
      setIsPsychedelic: React.Dispatch<React.SetStateAction<boolean>>;
      isToggled: boolean;
      setIsToggled: React.Dispatch<React.SetStateAction<boolean>>;
    }
  | undefined
>(undefined);

export const ToggleProvider = ({ children }: { children: React.ReactNode }) => {
  const [isWinking, setIsWinking] = useState(false);
  const [isPsychedelic, setIsPsychedelic] = useState(false);
  const [isToggled, setIsToggled] = useState(false);
  const isPotato = useIsPotato();
  const hasMouse = useHasMouse();

  const handlePsychedelic = (isClicked: boolean) => {
    if (isPotato || !hasMouse) {
      return;
    }
    setIsPsychedelic(isClicked);
  };

  const handleWinking = (isClicked: boolean) => {
    if (isPotato || !hasMouse) {
      return;
    }
    setIsWinking(isClicked);
  };

  return (
    <ToggleContext.Provider
      value={{
        isWinking,
        setIsWinking: handleWinking,
        isPsychedelic,
        setIsPsychedelic: handlePsychedelic,
        isToggled,
        setIsToggled,
      }}
    >
      {children}
    </ToggleContext.Provider>
  );
};

export const useToggleContext = () => {
  const context = useContext(ToggleContext);
  if (context) {
    return context;
  }
  return {
    isWinking: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setIsWinking: () => {},
    isPsychedelic: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setIsPsychedelic: () => {},
    isToggled: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setIsToggled: () => {},
  };
};

const ToggleStack = styled(Stack)(({ theme }) => {
  return {
    zIndex: 2,
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
    alignItems: 'center',
    '&.loaded': {
      opacity: 1,
    },
  };
});

const StyledBtn = styled(Btn)({});

const Toggle = ({ sx }: { sx?: SxProps<ITheme> }): React.ReactElement => {
  const { setIsWinking, setIsPsychedelic, setIsToggled } = useToggleContext();
  const { isPlaidSansLoaded } = useCriticalRenderContext();
  const [primaryRef, { width: primaryWidth }] = useMeasure();
  const hasMouse = useHasMouse();

  const doPsychedelic = (isClicked: boolean) => {
    setIsToggled(isClicked);

    // Psychedelic mode is a bit buggy on mobile,
    // so only allowing it on non-mobile sizes
    if (hasMouse) {
      setIsPsychedelic(isClicked);
    }
  };

  return (
    <ToggleStack
      gap={{ xs: 1, sm: 2 }}
      direction='row'
      className={isPlaidSansLoaded ? 'loaded' : ''}
      sx={sx}
      justifyContent={{ xs: 'center', sm: 'flex-start' }}
      flexWrap={{ xs: 'nowrap' }}
    >
      <StyledBtn
        href='#contactForm'
        variant='primary'
        ref={primaryRef}
        onMouseOver={() => {
          return setIsWinking(true);
        }}
        onFocus={() => {
          return setIsWinking(true);
        }}
        onMouseLeave={() => {
          return setIsWinking(false);
        }}
        onBlur={() => {
          return setIsWinking(false);
        }}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'inline-flex',
          fontSize: { xs: '1.6rem', md: '1.8rem', lg: '2.0rem' },
        }}
      >
        Talk with our team
      </StyledBtn>
      <BtnPrimary
        href='https://dashboard.plaid.com/signup'
        onMouseOver={() => {
          return doPsychedelic(true);
        }}
        onFocus={() => {
          return doPsychedelic(true);
        }}
        onMouseLeave={() => {
          return doPsychedelic(false);
        }}
        onBlur={() => {
          return doPsychedelic(false);
        }}
        sx={{
          width: primaryWidth ? `${primaryWidth}px` : 'auto',
          justifyContent: 'center',
          fontSize: { xs: '1.6rem', md: '1.8rem', lg: '2.0rem' },
        }}
      >
        Start building
      </BtnPrimary>
    </ToggleStack>
  );
};

export default Toggle;
